import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Truck, Package, Box, Scale } from 'lucide-react';
import { DriverDetails, vehicleTypeLabels } from '@/types/driver.types';

interface DriverVehicleInfoProps {
  driver: DriverDetails;
}

export function DriverVehicleInfo({ driver }: DriverVehicleInfoProps) {
  const capacity = driver.capacity;
  const vehicleType = driver.vehicle_type || '';
  const vehicleLabel = vehicleTypeLabels[vehicleType]?.ar || vehicleType || '-';
  const capacityName = driver.capacity_name?.ar || driver.capacity_name?.en || '-';

  const specs = capacity ? [
    {
      icon: Package,
      label: 'أقصى عدد شحنات',
      value: capacity.max_shipments,
      unit: 'شحنة',
    },
    {
      icon: Box,
      label: 'أقصى حجم',
      value: capacity.max_capacity_m3,
      unit: 'م³',
    },
    {
      icon: Scale,
      label: 'أقصى وزن',
      value: capacity.max_weight_kg,
      unit: 'كجم',
    },
  ] : [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Truck className="h-4 w-4" />
          معلومات المركبة
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Vehicle Type */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted text-2xl">
              {getVehicleEmoji(vehicleType)}
            </div>
            <div>
              <p className="text-xs text-muted-foreground">نوع المركبة</p>
              <p className="text-sm font-medium">{vehicleLabel}</p>
            </div>
          </div>
          <Badge variant="secondary" className="text-xs">
            {capacityName}
          </Badge>
        </div>

        {/* Capacity Status */}
        {capacity && (
          <div className="flex items-center gap-2">
            <Badge 
              variant="outline"
              className={
                capacity.is_active 
                  ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-800"
                  : "bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800"
              }
            >
              {capacity.is_active ? 'السعة نشطة' : 'السعة غير نشطة'}
            </Badge>
          </div>
        )}

        {/* Specifications */}
        {specs.length > 0 && (
          <div className="grid grid-cols-3 gap-3 pt-3 border-t">
            {specs.map((spec) => {
              const Icon = spec.icon;
              return (
                <div key={spec.label} className="text-center p-2 rounded-lg bg-muted/50">
                  <Icon className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
                  <p className="text-lg font-bold">{spec.value}</p>
                  <p className="text-xs text-muted-foreground">{spec.unit}</p>
                </div>
              );
            })}
          </div>
        )}

        {!capacity && (
          <p className="text-sm text-muted-foreground">لا توجد بيانات سعة مرتبطة</p>
        )}
      </CardContent>
    </Card>
  );
}

function getVehicleEmoji(type: string): string {
  const emojis: Record<string, string> = {
    motorcycle: '🏍️',
    van: '🚐',
    truck: '🚛',
    car: '🚗',
    bicycle: '🚲',
  };
  return emojis[type] || '🚗';
}
